import { useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { navLinks } from "../navData";
import { Menu, X, Sun, Moon, User } from "lucide-react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebaseConfig";

import { MobileNavDrawer } from "../components/MobileNavDrawer";
import MyAccountProfileDrawer from "../components/MyAccountProfileDrawer";
import MyAccountDrawer from "../components/MyAccountDrawer";
import ro from "/src/content/ro.json";

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  });
  const [currentUser, setCurrentUser] = useState(null);

  // Verificam autentificarea utilizatorului
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });
    return () => unsubscribe();
  }, []);

  // Schimba tema
  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    const root = document.documentElement;
    root.setAttribute("data-theme", newTheme);
    newTheme === "dark"
      ? root.classList.add("dark")
      : root.classList.remove("dark");
    localStorage.setItem("theme", newTheme);
    setTheme(newTheme);
  };

  // Aplica efect vizual pentru nav + verificare email
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");

    const nav = document.querySelector("nav");
    if (nav) {
      const isDark = theme === "dark";
      const glassLight = "#ffffffcc";
      const glassDark = "rgba(0,0,0,0.85)";
      nav.style.backgroundColor = isDark ? glassDark : glassLight;
      nav.style.borderBottom = isDark ? "1px solid #fff" : "1px solid #000";
      nav.style.backdropFilter = "blur(20px)";
    }
  }, [theme]);

  // GSAP scroll effect
  useGSAP(() => {
    const isDark = document.documentElement.getAttribute("data-theme") === "dark";
    const glassLight = "#ffffffcc";
    const glassDark = "rgba(0,0,0,0.85)";
    const borderLight = "1px solid #000";
    const borderDark = "1px solid #fff";

    const navTween = gsap.timeline({
      scrollTrigger: {
        trigger: "body",
        start: "top top",
        end: "100px top",
        scrub: true,
      },
    });

    navTween.fromTo(
      "nav",
      {
        backgroundColor: "transparent",
        backdropFilter: "blur(0px)",
        borderBottom: isDark ? borderDark : borderLight,
      },
      {
        backgroundColor: isDark ? glassDark : glassLight,
        backdropFilter: "blur(20px)",
        borderBottom: isDark ? borderDark : borderLight,
        duration: 0.8,
        ease: "power2.out",
      }
    );
  }, [theme]);

  return (
    <nav className="fixed top-0 w-full z-50 transition-all border-b border-black shadow-sm bg-transparent backdrop-blur-none h-[60px] flex items-center">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 h-full flex flex-row items-center">
        {/* === HEADER ROW – no wrap === */}
        <div className="flex items-center justify-between gap-10 sm:gap-30 md:gap-100 flex-nowrap w-full h-full">
          {/* BRAND (logo + titluri) */}
          <a
            href="#hero"
            className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1"
          >
            <img
              src="/images/logostefan1.png"
              alt={ro.nav.logoAlt}
              className="h-10 w-auto sm:h-12 md:h-12 shrink-0"
            />
            <div className="flex flex-col leading-tight text-left text-black min-w-0">
              {/* permite textului sa se "stranga", nu impinge butoanele */}
              <span className="text-base sm:text-lg md:text-xl font-bold truncate">
                {ro.nav.brand.titleTop}
              </span>
              <span className="text-sm sm:text-base md:text-lg font-bold truncate">
                {ro.nav.brand.titleBottom}
              </span>
            </div>
          </a>

          {/* CONTROALE MOBIL (nu cad pe rand nou) */}
          <div className="flex items-center gap-2.5 ml-2 shrink-0 md:hidden">
            <button
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border transition text-black"
              onClick={handleThemeToggle}
              aria-label={ro.nav.aria.themeToggle}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              className="text-black"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label={ro.nav.aria.openMenu}
            >
              {drawerOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* CONTROALE DESKTOP */}
          <div className="hidden md:flex items-center shrink-0">
            <ul className="flex items-center gap-6 text-[12px] flex-nowrap">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={`#${link.id}`}
                    className="cursor-pointer hover:underline text-black whitespace-nowrap"
                  >
                    {link.title}
                  </a>
                </li>
              ))}
              <li>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border transition text-black"
                  onClick={handleThemeToggle}
                  aria-label={ro.nav.aria.themeToggle}
                >
                  {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                </button>
              </li>
              <li>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border transition text-black hover:text-orange-500"
                  onClick={() => setAccountOpen(!accountOpen)}
                  aria-label={ro.nav.aria.myAccount}
                >
                  <User size={18} />
                </button>
                {currentUser ? (
                  <MyAccountProfileDrawer
                    open={accountOpen}
                    onClose={() => setAccountOpen(false)}
                  />
                ) : (
                  <MyAccountDrawer
                    open={accountOpen}
                    onClose={() => setAccountOpen(false)}
                  />
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Drawer mobil neschimbat */}
      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        navLinks={navLinks}
        theme={theme}
        onThemeToggle={handleThemeToggle}
        currentUser={currentUser}
        onProfileClick={() => setAccountOpen(true)}
      />
    </nav>
  );
};

export default Navbar;
